import { failLoud } from '@resource-kit/core';
import type { ModelName, ModelRegistry } from '@resource-kit/core';
import type { ListParams } from '@resource-kit/model';
import type { ListResult } from '../list/types';

/**
 * What a relation TARGET must offer for a many2one field to render (design
 * §5.11). The two data members mirror the members of the same names on
 * `ResourceAPI` (`../react/bundle`), which carries no type-level link back to
 * here; the two are held in step at the assignment site —
 * `registerRelationModel('supplier', …)` in the host's `features/relations.ts`
 * passes those `host.api` members straight through, so a drift in either
 * signature is a compile error there. Both speak the package's own vocabulary
 * — bare records and `ListResult`, never a host envelope (#2167 PR-2,
 * ADR-0035 D4) — which is what keeps the mirror exact. They are two different
 * endpoints on purpose:
 *
 *   `getMany`         — read-only mode. The record already holds the foreign
 *                       id, so the display name is a batch id → row lookup.
 *   `searchReference` — edit mode. The operator types "Acme" and holds NO id,
 *                       so candidates can only come from a remote search.
 *
 * The record type is erased here so one catalog can hold every target; the
 * narrowed form a registration is checked against is `ReferenceSourceFor<N>`.
 */
export interface ReferenceSource {
  getMany: (ids: readonly string[]) => Promise<unknown[]>;
  searchReference: (query: string, params?: ListParams) => Promise<ListResult<unknown>>;
  /** Column holding the target's id — the key rows are re-homed by. */
  idField: string;
  /** Column holding the target's display name (`ModelDef.recName`). */
  recName: string;
  /** Where the read-only link points. */
  detailPath: (id: string) => string;
}

/**
 * A relation target's source, narrowed to the record its registry row declares.
 *
 * INTERSECTS `ReferenceSource` for the same reason `WidgetFor` intersects
 * `Widget`: it makes the narrowed form provably storable in the erased catalog
 * even for an UNRESOLVED `N`, which is what lets `registerRelationModel<N>`
 * hold it without a cast.
 */
export type ReferenceSourceFor<N extends ModelName> = ReferenceSource & {
  getMany: (ids: readonly string[]) => Promise<ModelRegistry[N]['record'][]>;
  searchReference: (
    query: string,
    params?: ListParams,
  ) => Promise<ListResult<ModelRegistry[N]['record']>>;
  idField: Extract<keyof ModelRegistry[N]['record'], string>;
  recName: Extract<keyof ModelRegistry[N]['record'], string>;
};

/**
 * The relation-target catalog (#1804 batch C).
 *
 * The CATALOG is framework-free algebra and lives at this package's root: a
 * `Map`, a name closure and a failure policy, with nothing React about it. What
 * a host REGISTERS into it closes over that host's HTTP clients and route
 * paths — but those arrive as functions through `ReferenceSource`, so the
 * registry never learns of them (ADR-0035 D1).
 *
 * Registration is EXPLICIT (design §5.2 discipline 2): features export a
 * `registerXxxRelations()` and the composition root calls them through
 * `src/features/relations.ts`. A bare side-effect import would leave the
 * ordering to the bundler, which is free to drop it.
 *
 * Membership in `ModelRegistry` and presence in this map are DIFFERENT facts,
 * and only the first is a compile-time one. A declared name with no
 * registration is an authoring mistake that surfaces the first time a field
 * pointed at it renders — so it goes through `failLoud`: it stops the world in
 * dev and test, and in production it logs and hands the caller back `null`,
 * whose documented fallback is the raw id the link was meant to replace.
 */
const sources = new Map<string, ReferenceSource>();

/**
 * Register a relation target. `N` is constrained to the declared model names,
 * so a typo is a compile error, and the source is narrowed FROM `N`, so an
 * `idField` / `recName` naming a column the target record does not have cannot
 * be registered.
 */
export function registerRelationModel<N extends ModelName>(
  name: N,
  source: ReferenceSourceFor<N>,
): void {
  if (sources.has(name)) {
    failLoud(`relation target "${name}" is registered twice`);
    return;
  }
  sources.set(name, source);
}

/**
 * The reference source a `relation` field resolves through, or `null` when the
 * target was never registered (after reporting it — see the note above).
 */
export function resolveRelationModel(name: string): ReferenceSource | null {
  const source = sources.get(name);
  if (!source) {
    failLoud(`relation target "${name}" is not registered`);
    return null;
  }
  return source;
}

/** Test seam — the catalog is module state, so suites must start from empty. */
export function resetRelationRegistry(): void {
  sources.clear();
}
